import { Fragment, PureComponent, ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import NavBar from '../common/NavBar';
import NewHeaderComponent from '../common/NewHeaderComponent';
import { billIcon, kebabIcon, MedicalHistory, userIcon, Vitals } from '../common/Icons';
import CountContainer from './CountContainer';
import Table, { ColumnGroupType, ColumnType } from 'antd/lib/table';
import moment from 'moment';
import FetchError from './FetchError';
import EmptyView from './EmptyView';
import { AntTooltip } from '../common/AntTooltip';
import ipdAdmitPatientsList, { Data, Insurance, Patient } from '../../api/ipdAdmitPatientsList';
import { captureError, captureErrorString } from '../../pages/captureError';
import { track } from '../../tracking';
import { Dropdown, Menu, notification } from 'antd';
import { fetchAndStoreIpdData } from '../../store/actions/app';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../../store/reducers';
import { Actions } from '../../store/actions';
import { connect } from 'react-redux';
import {
    GO_TO_ADD_NEW_PATIENT,
    GO_TO_PATIENTS_DETAILS,
    GO_TO_PRESCRIPTION_PAD_V2,
    IPD_LIST,
} from '../../routes/paths';
import {
    getDoctorSelectorList,
    getPatientByLocalId,
    getSelectedClinicId,
} from '../../store/idbHandler';
import { his } from '../../pages/history';
import { formattedNumber, getDayWord } from '../../pages/utils';
import { getIpdConfig } from '../../store/localstorage';
import { Duration } from '../../api/ipdAdmitPatient';
import IpdBilling from './IpdBilling';
import ipdAdmissionUpdate from '../../api/ipdAdmissionUpdate';
import { RouteComponentProps } from 'react-router-dom';
import { initVisit } from '../../store/actions/visit';
import runAIPrompt, { Response } from '../../api/runAIPrompt';
import MedicalCertificateDialog from '../common/MedicalCertificateDialog';
import { CUSTOM_CERT_ID } from '../common/medicalCertificates';
import { OptionItem } from '../../pages/profile/OptionItem';

type FetchState = 'loading' | 'success' | 'error' | '';

const statusObject: { [k: string]: { label: string; className: string } } = {
    AD: {
        label: 'Admitted',
        className: 'border-darwin-green-primary text-darwin-green-primary bg-darwin-green-bg-light',
    },
    DC: {
        label: 'Discharged',
        className:
            'border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800 bg-darwin-accent-bright-blue-100',
    },
};

interface State {
    // isAdmitPatientModalVisible: boolean;
    fetchState: FetchState;
    lastUpdated: string;
    data: Data[];
    activeItem?: Data;
    dischargeResponse?: Response & { admission: Data };
    isDischargeLoading?: string;
}

interface Props extends RouteComponentProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
}

class IpdList extends PureComponent<Props> {
    state: State = {
        // isAdmitPatientModalVisible: false,
        fetchState: '',
        lastUpdated: new Date().toISOString(),
        data: [],
    };

    beds = getIpdConfig()?.beds || [];
    rooms = getIpdConfig()?.rooms || [];

    userIcon = userIcon();
    kebabIcon = kebabIcon();
    billIcon = billIcon('fill-white w-14 h-14');

    componentDidMount = (): void => {
        const { fetchIpdPatientsList } = this;
        fetchIpdPatientsList();
        const { dispatch } = this.props;
        dispatch(fetchAndStoreIpdData());
    };

    fetchIpdPatientsList = async () => {
        this.setState({ fetchState: 'loading' });

        try {
            const { success, data = [], error = '' } = await ipdAdmitPatientsList();
            if (success) {
                this.setState({
                    fetchState: 'success',
                    data: data,
                    lastUpdated: new Date().toISOString(),
                });
            } else {
                captureErrorString(error || 'Error in fetching ipd cases');
                notification.error({
                    message: error || 'Error in fetching ipd cases',
                    duration: 5,
                });
            }
        } catch (error) {
            this.setState({ fetchState: 'error' });
            captureError(error as Error);
            track('ERROR', {
                on: 'fetchIpdPatientsList',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error in fetching ipd cases',
                duration: 5,
            });
        }
    };

    // toggleIsAdmitPatientModalVisible = () => {
    //     const { isAdmitPatientModalVisible } = this.state;
    //     this.setState({ isAdmitPatientModalVisible: !isAdmitPatientModalVisible });
    // };

    onAdmitPatient = () => {
        const clinicId = getSelectedClinicId() || '';
        his.push(GO_TO_ADD_NEW_PATIENT(clinicId));
    };

    onViewDetail = (id: string) => {
        his.push(GO_TO_PATIENTS_DETAILS(id, 'visit'));
    };

    onViewMore = (item?: Data) => {
        this.setState({ activeItem: item });
    };

    onDischarge = async (item: Data) => {
        if (item.id) {
            await ipdAdmissionUpdate(item.id, {
                status: 'DC',
            });
        }
        this.componentDidMount();
    };

    onGenerateDischarge = async (item: Data) => {
        const { isDischargeLoading } = this.state;
        if (isDischargeLoading) {
            notification.error({
                message: 'Discharge Certificate is already being generated',
                duration: 2,
            });
            return;
        }
        const { id } = item;
        try {
            notification.info({
                message: 'Discharge Certificate',
                description: 'Generating discharge certificate',
                duration: 10,
            });
            this.setState({ isDischargeLoading: item.id });
            const res = await runAIPrompt({
                pt_oid: item.patient.id,
                input_variables: {
                    curr_date: new Date().toISOString(),
                },
                ipd_id: id,
                docid: getDoctorSelectorList()[0].id,
            });
            notification.destroy();
            this.setState({ dischargeResponse: { ...res, admission: item } });
        } catch (e) {
            console.log(e);
            notification.error({
                message: 'Error in generating discharge certificate',
                duration: 5,
            });
        } finally {
            this.setState({ isDischargeLoading: undefined });
        }
    };

    onRx = async (item: Data, type: 'vitals' | 'medicalHistory'): Promise<void> => {
        const { dispatch, history } = this.props;
        const onSuccess = (prescriptionId: string) => {
            const queryString = new URLSearchParams({
                components: type,
                action: 'save',
                exit: IPD_LIST,
            }).toString();
            history.push({
                pathname: GO_TO_PRESCRIPTION_PAD_V2(prescriptionId, 'edit', undefined, undefined),
                search: `?${queryString}`,
            });
        };
        const { patient } = item;
        const localPt = await getPatientByLocalId(patient.id);
        const { id } = patient;
        const { personal } = patient.profile;
        const { name, age, gender, phone } = personal;
        const initVisitAction = initVisit(
            {
                localId: id,
                id: id,
                uuid: patient.uuid,
                name: name,
                phCode: phone?.c,
                phoneNumber: phone?.n,
                age: age,
                gender: gender?.toString() || '',
                formData: patient.formData,
                abha: Array.isArray(localPt.abhaV2)
                    ? localPt.abhaV2?.find((abha) => abha.cId === getSelectedClinicId())
                    : localPt?.abha,
                relations: localPt.relations,
            },
            getDoctorSelectorList()[0].id,
            getSelectedClinicId() || '',
            undefined,
            onSuccess,
            undefined,
            { autoCopy: false, avoidTriggerBEStartVisit: true },
        );
        track('Start_Visit', { from: 'Pt_Details' });
        initVisitAction && dispatch(initVisitAction);
    };

    renderComponent = (transactions: Data[]) => {
        const { isDischargeLoading } = this.state;
        const columns: (ColumnGroupType<Data> | ColumnType<Data>)[] = [
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">Seq</span>
                ),
                width: 75,
                key: 'index',
                render: (_: Data, __: Data, index: number): ReactNode => {
                    return (
                        <p className="text-14 text-darwin-neutral-900 whitespace-nowrap">
                            {index + 1}
                        </p>
                    );
                },
                fixed: 'left',
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">Date</span>
                ),
                width: 140,
                key: 'date',
                dataIndex: 'date',
                render: (date: string): ReactNode => {
                    return (
                        <AntTooltip content={moment(date).format('DD/MM/YYYY hh:mm a ')}>
                            <p className="w-max">{getDayWord(date, "DD MMM' YY")}</p>
                        </AntTooltip>
                    );
                },
                sorter: ({ date: a = '' }, { date: b = '' }): number =>
                    moment(a).format('YYYY-MM-DD') < moment(b).format('YYYY-MM-DD') ? -1 : 1,
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">Ipid</span>
                ),
                width: 140,
                key: 'ipid',
                dataIndex: 'ipid',
                render: (ipid: string, record: Data): ReactNode => {
                    // return <p>{'IP48197' || ipid}</p>;
                    return (
                        <div>
                            <p>{ipid}</p>
                            <span
                                className="text-11 text-actionPrimary select-all cursor-pointer"
                                onClick={() => {
                                    this.props.history.push(
                                        GO_TO_PATIENTS_DETAILS(
                                            record.patient.id,
                                            'patient-overview',
                                        ),
                                    );
                                }}
                            >
                                {record.patient.profile.personal.name}
                            </span>
                        </div>
                    );
                },
                sorter: (a, b) => (a.ipid || '').localeCompare(b.ipid || ''),
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">Uhid</span>
                ),
                width: 120,
                key: 'patient',
                dataIndex: 'patient',
                render: (patient: Patient): ReactNode => {
                    const uhid = patient.formData?.find((fd) => fd.key === 'uhid')?.value;
                    // return <p>{'AK76769' || uhid || '-'}</p>;
                    return <p>{uhid || '-'}</p>;
                },
                sorter: (a, b) => {
                    const aUhid = String(
                        a.patient.formData?.find((fd) => fd.key === 'uhid')?.value || '',
                    );
                    const bUhid = String(
                        b.patient.formData?.find((fd) => fd.key === 'uhid')?.value || '',
                    );
                    return aUhid.localeCompare(bUhid);
                },
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Bed | Room
                    </span>
                ),
                width: 200,
                key: 'bed',
                dataIndex: 'bed',
                render: (bed: string): ReactNode => {
                    // return <p>{'6 | Room B' || bed}</p>;
                    return (
                        <p>
                            {[
                                this.beds.find((t) => t.id === bed)?.name,

                                this.rooms.find(
                                    (t) => t.id === this.beds.find((t) => t.id === bed)?.room,
                                )?.name,
                            ]
                                .filter(Boolean)
                                .join(' | ') || '-'}
                        </p>
                    );
                },
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Status
                    </span>
                ),
                width: 150,
                key: 'status',
                dataIndex: 'status',
                render: (status: string): ReactNode => {
                    const { label, className } = statusObject[status] || {};
                    return (
                        <p className={`capitalize w-max px-12 py-4 border rounded-4 ${className}`}>
                            {label}
                        </p>
                    );
                },
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Dept | Doctor
                    </span>
                ),
                width: 180,
                key: 'dept_doctor',
                dataIndex: 'dept_doctor',
                render: (_, item: Data): ReactNode => {
                    return (
                        <div className="space-y-4">
                            {/* <p>{'Pediatrics' || item.department.name || '-'}</p> */}
                            <p>{item?.department?.name || '-'}</p>
                            {/* <p className="text-darwin-neutrals-600">
                                {'Dr. Megha Kapoor' || item.doc.name || '-'}
                            </p> */}
                            <p className="text-darwin-neutrals-600 text-11">
                                {'Dr ' + item?.doc?.name || '-'}
                            </p>
                        </div>
                    );
                },
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Insurance Type
                    </span>
                ),
                width: 140,
                key: 'insurance',
                dataIndex: 'insurance',
                render: (insurance: Insurance): ReactNode => {
                    // return <p className="capitalize">{'Insurance' || billing_type || '-'}</p>;
                    return <p className="capitalize">{insurance?.type || '-'}</p>;
                },
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Pending bill
                    </span>
                ),
                width: 130,
                key: 'pending_bill',
                dataIndex: 'pending_bill',
                render: (_, record: Data): ReactNode => {
                    const { totalBilled = 0, totalPaid = 0 } = record;
                    const pendingBill = totalBilled - totalPaid;
                    return (
                        <p className="capitalize">{'₹' + (formattedNumber(pendingBill) || '-')}</p>
                    );
                },
                sorter: (a, b) => {
                    const { totalBilled: aTotalBilled = 0, totalPaid: aTotalPaid = 0 } = a;
                    const { totalBilled: bTotalBilled = 0, totalPaid: bTotalPaid = 0 } = b;
                    const aPendingBill = aTotalBilled - aTotalPaid;
                    const bPendingBill = bTotalBilled - bTotalPaid;
                    return aPendingBill - bPendingBill;
                },
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Duration
                    </span>
                ),
                width: 110,
                key: 'duration',
                dataIndex: 'duration',
                render: (duration: Duration): ReactNode => {
                    const value = duration.value || '';
                    const unit = duration.unit || '';
                    return <p>{value && unit ? `${value} ${unit}` : '-'}</p>;
                },
            },
            {
                title: (
                    <p className="flex justify-end">
                        <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                            Actions
                        </span>
                    </p>
                ),
                width: 250,
                key: 'patient',
                dataIndex: 'patient',
                render: (_, item: Data): ReactNode => {
                    return (
                        <div className="flex items-center space-x-8 justify-end">
                            <button
                                className="flex items-center space-x-8 secondary-btn-v3 btn--small px-14 py-8"
                                onClick={() => this.onRx(item, 'vitals')}
                            >
                                <p>{Vitals('fill-darwin-accent-bright-blue-800 w-14 h-14')}</p>
                                <p>Vitals</p>
                            </button>
                            <button
                                className="flex items-center space-x-8 primary-btn-v3 btn--small px-14 py-8"
                                onClick={() => this.onViewMore(item)}
                            >
                                <p>{this.billIcon}</p>
                                <p>Billing</p>
                            </button>
                            <Dropdown
                                overlay={
                                    <Menu>
                                        <OptionItem
                                            onClick={() => this.onRx(item, 'medicalHistory')}
                                            label="Add Medical History"
                                            leftIcon={MedicalHistory(
                                                'relative top-4 fill-darwin-accent-bright-blue-800 w-14 h-14',
                                            )}
                                        />
                                        <OptionItem
                                            onClick={() => this.onGenerateDischarge(item)}
                                            label="Generate discharge summary"
                                            leftIcon={
                                                isDischargeLoading === item.id ? (
                                                    <svg
                                                        className="w-14 h-14 fill-actionPrimary animate-spin relative top-3"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 512 512"
                                                    >
                                                        <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 384 512"
                                                        className="w-14 h-14 relative top-3"
                                                    >
                                                        <path
                                                            d="M320 464c8.8 0 16-7.2 16-16V160H256c-17.7 0-32-14.3-32-32V48H64c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16H320zM0 64C0 28.7 28.7 0 64 0H229.5c17 0 33.3 6.7 45.3 18.7l90.5 90.5c12 12 18.7 28.3 18.7 45.3V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64z"
                                                            fill="currentColor"
                                                        ></path>
                                                    </svg>
                                                )
                                            }
                                        />
                                        <OptionItem
                                            onClick={() => this.onDischarge(item)}
                                            label="Discharge"
                                            leftIcon={
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    height="1em"
                                                    viewBox="0 0 512 512"
                                                    className="w-14 h-14 relative top-3"
                                                >
                                                    <path
                                                        d="M505 273c9.4-9.4 9.4-24.6 0-33.9L377 111c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l87 87L184 232c-13.3 0-24 10.7-24 24s10.7 24 24 24l246.1 0-87 87c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0L505 273zM168 80c13.3 0 24-10.7 24-24s-10.7-24-24-24L88 32C39.4 32 0 71.4 0 120L0 392c0 48.6 39.4 88 88 88l80 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-80 0c-22.1 0-40-17.9-40-40l0-272c0-22.1 17.9-40 40-40l80 0z"
                                                        fill="currentColor"
                                                    ></path>
                                                </svg>
                                            }
                                        />
                                    </Menu>
                                }
                                placement="bottomLeft"
                                arrow
                                trigger={['click']}
                                overlayClassName="z-20"
                            >
                                <button className="p-10">{this.kebabIcon}</button>
                            </Dropdown>
                        </div>
                    );
                },
                fixed: 'right',
            },
        ];
        return this.state.fetchState === 'loading' ? (
            <div className="h-full flex items-center justify-center bg-white">
                <svg
                    className="w-24 h-24 fill-actionPrimary animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                </svg>
            </div>
        ) : this.state.fetchState !== 'error' && transactions.length ? (
            <Table
                columns={columns}
                className="antd-table-dashboard"
                dataSource={transactions}
                pagination={false}
                sticky={true}
                scroll={{
                    x: 900,
                }}
            />
        ) : this.state.fetchState === 'error' ? (
            <FetchError onRefresh={this.componentDidMount} />
        ) : (
            <EmptyView />
        );
    };

    closeMedCert = async () => {
        this.setState({ dischargeResponse: undefined });
    };

    render() {
        const { lastUpdated, data, activeItem, dischargeResponse } = this.state;
        const { userIcon, onAdmitPatient, onViewMore } = this;

        return (
            <Fragment>
                {dischargeResponse && (
                    <MedicalCertificateDialog
                        patient={{
                            localId: dischargeResponse.admission.patient.id,
                            id: dischargeResponse.admission.patient.id,
                            personal: {
                                name: {
                                    f: dischargeResponse.admission.patient.profile.personal.name,
                                },
                                phone: dischargeResponse.admission.patient.profile.personal.phone,
                                age: dischargeResponse.admission.patient.profile.personal.age,
                                gender: dischargeResponse.admission.patient.profile.personal.gender,
                            },
                        }}
                        closeMe={this.closeMedCert}
                        defaultState={{
                            page: 'create-cr',
                            type: 'mcert',
                            heading: 'Discharge Certificate',
                            selectedCertificate: CUSTOM_CERT_ID,
                        }}
                        customCertificateContent={dischargeResponse.text}
                        docid={getDoctorSelectorList()[0].id}
                        // isMarkdown
                    />
                )}
                <div className="grid h-full overflow-x-auto overflow-y-hidden grid-cols-queueGrid min-w-840 text-13">
                    <Helmet>
                        <meta charSet="utf-8" />
                        <title>EKA - Ipd</title>
                    </Helmet>
                    <NavBar />
                    <div className="flex flex-col h-full col-span-11 overflow-hidden">
                        <NewHeaderComponent
                            title="IPD"
                            filterComponent={
                                <div className="flex w-full justify-between items-stretch gap-8">
                                    <div className="flex relative top-2 items-center gap-4">
                                        <p className="text-neutral-5">
                                            Last updated at {moment(lastUpdated).format('hh:mm A')}
                                        </p>
                                        <AntTooltip content="Refresh">
                                            <div className="flex justify-between p-4 select-none items-center space-x-16">
                                                <div className="flex space-x-8 items-center">
                                                    <button
                                                        onClick={this.componentDidMount}
                                                        className="flex space-x-8 items-center border-b border-transparent cursor-pointer text-actionPrimary hover:text-actionPrimary-hover hover:border-actionPrimary-hover font-700 focus:outline-none text-14"
                                                    >
                                                        <svg
                                                            width="12"
                                                            height="12"
                                                            className="fill-darwin-accent-bright-blue-800"
                                                            viewBox="0 0 512 512"
                                                        >
                                                            <path d="M370.72 133.28C339.458 104.008 298.888 87.962 255.848 88C178.39 88.068 111.52 141.178 93.057 214.85C91.713 220.213 86.935 224 81.406 224H24.103C16.605 224 10.909 217.193 12.296 209.824C33.933 94.924 134.813 8 256 8C322.448 8 382.791 34.136 427.315 76.685L463.03 40.97C478.149 25.851 504 36.559 504 57.941V192C504 205.255 493.255 216 480 216H345.941C324.559 216 313.851 190.149 328.97 175.029L370.72 133.28V133.28ZM32 296H166.059C187.441 296 198.149 321.851 183.03 336.971L141.28 378.721C172.542 407.994 213.115 424.04 256.156 424.001C333.574 423.931 400.471 370.857 418.943 297.152C420.287 291.789 425.065 288.002 430.594 288.002H487.898C495.396 288.002 501.092 294.809 499.705 302.178C478.067 417.076 377.187 504 256 504C189.552 504 129.209 477.864 84.685 435.315L48.97 471.03C33.851 486.149 8 475.441 8 454.059V320C8 306.745 18.745 296 32 296Z"></path>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </AntTooltip>
                                    </div>
                                    <div className="flex gap-8 h-36 items-center">
                                        {/* <DateFilter
                                        currTimeFilter={this.state.currTimeFilter}
                                        daysFilter={this.daysFilter}
                                        setTimeFilter={(startDate: string, endDate: string) => {
                                            this.setState({
                                                currTimeFilter: 'Custom',
                                            });
                                            this.setTimeFilter(startDate, endDate);
                                        }}
                                        datePickerDisabled={this.state.fetchState === 'loading'}
                                        noOfDays={90}
                                        dates={this.state.dates}
                                        setDates={(val) => this.setState({ dates: val })}
                                    /> */}

                                        <button
                                            className="primary-btn-v3 btn--small flex items-center space-x-8 px-16 py-8"
                                            // onClick={toggleIsAdmitPatientModalVisible}
                                            onClick={onAdmitPatient}
                                        >
                                            <p>Admit Patient</p>
                                            <p>{userIcon}</p>
                                        </button>
                                    </div>
                                </div>
                            }
                        />

                        <div className="p-16 flex-1 flex flex-col space-y-12 bg-darwin-neutrals-50">
                            <div className="flex items-center justify-between border border-darwin-neutral_new-200 px-12 py-8 rounded-8 bg-white">
                                <CountContainer
                                    text="ADMITTED"
                                    count={data.length.toString() || '-'}
                                />
                                {/* <CountContainer text="discharged" count={'02'} /> */}
                                {/* <CountContainer text="others" count={'01'} /> */}
                                {/* <CountContainer text="Avaiable beds" count={'02'} showBorder /> */}
                                {/* <CountContainer text="occupied" count={'02'} /> */}
                                {/* <CountContainer text="others" count={'02'} /> */}
                                <CountContainer
                                    text="total pending"
                                    count={`₹ ${
                                        data
                                            .reduce(
                                                (acc, { totalBilled = 0, totalPaid = 0 }) =>
                                                    acc + (totalBilled - totalPaid),
                                                0,
                                            )
                                            .toLocaleString('en-IN') || '-'
                                    }`}
                                    className="text-red-primary"
                                    showBorder
                                />
                            </div>

                            <div className="relative flex-1 w-full table-fader border border-neutral-7 rounded-8 overflow-hidden">
                                {/* {switchColumn === 'loading' && (
                                <div className="flex items-center justify-center absolute inset-0 z-10 bg-white">
                                    <svg
                                        className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                    >
                                        <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                    </svg>
                                </div>
                            )} */}
                                <div className="absolute inset-0 overflow-y-scroll">
                                    {this.renderComponent(data)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {activeItem && (
                        <IpdBilling closeMe={() => onViewMore(undefined)} item={activeItem} />
                    )}
                </div>
            </Fragment>
        );
    }
}

export default connect()(IpdList);
